import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { matchPath, useLocation } from 'react-router'

import { routePaths } from '~/routes/routePaths'

/**
 * pathname に一致する route の name (i18n key) を解決する。
 * 動的セグメント (`:caseId` 等) を含む path は `matchPath` で判定するが、
 * `/plan/procurement` (静的) と `/plan/procurement/:id` (動的) のように
 * 前方一致しうる複数候補がある場合、 セグメント数が多い (= より具体的な) path を
 * 優先する必要があるため、 マッチした候補の中から path のセグメント数が最大の
 * ものを採用する。
 */
const resolveRouteNameKey = (pathname: string): string | undefined => {
  const matches = Object.values(routePaths).filter(
    (route) => matchPath(route.path, pathname) !== null,
  )
  if (matches.length === 0) return undefined
  const best = matches.reduce((mostSpecific, candidate) =>
    candidate.path.split('/').length > mostSpecific.path.split('/').length
      ? candidate
      : mostSpecific,
  )
  return best.name
}

/**
 * ブラウザタブタイトルを「{画面名} | eBalance」形式で維持する。
 * `docs/specs/common/shared-components.md` AC-SC-030 準拠。
 * `ProtectedMainLayout` (= 全画面共通の外殻) に配置し、 route 遷移のたびに
 * `document.title` を更新する。
 */
export const useDocumentTitle = (): void => {
  const { t } = useTranslation()
  const location = useLocation()

  useEffect(() => {
    const nameKey = resolveRouteNameKey(location.pathname)
    // [拡張] 48点データ画面は、取引契約一覧から counterparty (相対先名) 付きで
    // 別タブ遷移してくる (2026-08-31 デザイナー指摘「タブ名に対象相対先名を
    // 表示する」)。複数タブを開き比べる使い方のため、タブ名で対象を判別できる
    // よう「{画面名} ({相対先名}) | eBalance」とする。他画面は AC-SC-030 のまま。
    const counterparty =
      matchPath(routePaths.supply48Data.path, location.pathname) !== null
        ? new URLSearchParams(location.search).get('counterparty')
        : null
    const base = nameKey !== undefined ? t(nameKey) : undefined
    document.title =
      base !== undefined
        ? counterparty !== null && counterparty !== ''
          ? `${base} (${counterparty}) | eBalance`
          : `${base} | eBalance`
        : 'eBalance'
  }, [location.pathname, location.search, t])
}
